export const STOCK_QUANTITY_SCALE = 1_000;
export const STOCK_MAX_MILLIUNITS = 9_000_000_000_000_000;
export const STOCK_MAX_QUANTITY = '9000000000000';
export type StockQuantity = string;

const stockMaximumMilliunits = BigInt(STOCK_MAX_MILLIUNITS);
const stockDecimalPattern = /^(?<sign>-?)(?<units>\d+)(?:\.(?<fraction>\d{1,3}))?$/u;

interface ParsedStockQuantity {
  readonly canonical: string;
  readonly milliunits: bigint;
}

function parsedStockQuantity(value: string, signed: boolean): ParsedStockQuantity | null {
  const match = stockDecimalPattern.exec(value);
  if (match?.groups === undefined) return null;
  const unitGroup = match.groups.units;
  if (unitGroup === undefined) return null;
  const negative = match.groups.sign === '-';
  if (negative && !signed) return null;
  const units = unitGroup.replace(/^0+(?=\d)/u, '');
  const fraction = (match.groups.fraction ?? '').padEnd(3, '0');
  const absoluteMilliunits = BigInt(units) * BigInt(STOCK_QUANTITY_SCALE) + BigInt(fraction);
  if (absoluteMilliunits > stockMaximumMilliunits) return null;
  const milliunits =
    negative && absoluteMilliunits !== 0n ? -absoluteMilliunits : absoluteMilliunits;
  const canonicalFraction = fraction.replace(/0+$/u, '');
  const canonicalAbsolute = canonicalFraction === '' ? units : `${units}.${canonicalFraction}`;
  return {
    canonical: milliunits < 0n ? `-${canonicalAbsolute}` : canonicalAbsolute,
    milliunits,
  };
}

export function canonicalizeStockQuantity(value: string): string | null {
  return parsedStockQuantity(value, false)?.canonical ?? null;
}

export function canonicalizeSignedStockQuantity(value: string): string | null {
  return parsedStockQuantity(value, true)?.canonical ?? null;
}

export function stockQuantityToMilliunits(value: string): number {
  const parsed = parsedStockQuantity(value, false);
  if (parsed === null) throw new RangeError('STOCK_QUANTITY_INVALID');
  return Number(parsed.milliunits);
}

export function stockMilliunitsToQuantity(milliunits: number): string {
  if (!Number.isSafeInteger(milliunits) || milliunits < 0 || milliunits > STOCK_MAX_MILLIUNITS) {
    throw new RangeError('STOCK_MILLIUNITS_INVALID');
  }
  const units = Math.floor(milliunits / STOCK_QUANTITY_SCALE);
  const fraction = milliunits % STOCK_QUANTITY_SCALE;
  if (fraction === 0) return String(units);
  return `${units}.${String(fraction).padStart(3, '0').replace(/0+$/u, '')}`;
}

export function stockSignedMilliunitsToQuantity(milliunits: number): string {
  if (!Number.isSafeInteger(milliunits) || Math.abs(milliunits) > STOCK_MAX_MILLIUNITS) {
    throw new RangeError('STOCK_MILLIUNITS_INVALID');
  }
  if (milliunits < 0) return `-${stockMilliunitsToQuantity(-milliunits)}`;
  return stockMilliunitsToQuantity(milliunits);
}

export function isStockBalanceWithinBounds(total: number, available: number): boolean {
  return (
    Number.isSafeInteger(total) &&
    Number.isSafeInteger(available) &&
    total >= 0 &&
    available >= 0 &&
    available <= total &&
    total <= STOCK_MAX_MILLIUNITS
  );
}

export const STOCK_QUANTITY_AUTHORITY = {
  transport: 'CANONICAL_DECIMAL_STRING',
  storage: 'INTEGER_MILLIUNITS',
  scale: STOCK_QUANTITY_SCALE,
  minimumMilliunits: 0,
  maximumMilliunits: STOCK_MAX_MILLIUNITS,
  maximumUnits: STOCK_MAX_QUANTITY,
  maximumFractionDigits: 3,
} as const;
